// apps/widget/src/components/skin-type-card.tsx
//
// Skin type as a card of its own: an icon, the type's name, what it means, what
// usually causes it. A bare word like "combination" tells a patient nothing
// they can act on. The care advice for the type sits in the summary below.
//
// The icons are simple line drawings in the brand colour, so they follow the
// tenant's theme rather than carrying a palette of their own.

import { t } from '../lib/i18n';

const KNOWN = ['dry', 'oily', 'combination', 'normal', 'sensitive'] as const;
type SkinType = (typeof KNOWN)[number];

export function SkinTypeCard({ skinType }: { skinType: string }) {
  const type = (KNOWN as readonly string[]).includes(skinType) ? (skinType as SkinType) : null;

  return (
    <div className="gs-skintype">
      <div className="gs-skintype__head">
        {type && <SkinTypeIcon type={type} />}
        <div>
          <h2 className="gs-section-title gs-skintype__label">{t('report.skinTypeTitle')}</h2>
          <p className="gs-skintype__name">{type ? t(`skinTypeName.${type}`) : skinType}</p>
        </div>
      </div>
      {type && (
        <>
          <p className="gs-body">{t(`skinType.${type}`)}</p>
          <p className="gs-skintype__more">
            <b>{t('report.skinTypeCause')}</b> {t(`skinTypeCause.${type}`)}
          </p>
        </>
      )}
    </div>
  );
}

const DROP = 'M24 6 C24 6 11 21 11 30 a13 13 0 0 0 26 0 C37 21 24 6 24 6 Z';

function SkinTypeIcon({ type }: { type: SkinType }) {
  return (
    <svg
      className="gs-skintype__icon"
      viewBox="0 0 48 48"
      width="44"
      height="44"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {type === 'dry' && (
        <>
          {/* A drop with a crack through it: moisture that is not holding. */}
          <path d={DROP} />
          <path d="M22 18 l4 6 -5 5 4 6" />
        </>
      )}
      {type === 'oily' && (
        <>
          {/* A full drop with a highlight: shine. */}
          <path d={DROP} fill="currentColor" fillOpacity="0.85" />
          <path d="M18 30 a6 6 0 0 0 5 6" stroke="#fff" />
        </>
      )}
      {type === 'combination' && (
        <>
          {/* Half full: oily centre, drier sides. */}
          <path d={DROP} />
          <path d="M24 6 C24 6 11 21 11 30 a13 13 0 0 0 13 13 Z" fill="currentColor" fillOpacity="0.85" />
        </>
      )}
      {type === 'normal' && (
        <>
          {/* A leaf: balanced and healthy. */}
          <path d="M10 38 C10 20 22 10 39 9 C39 27 29 38 10 38 Z" />
          <path d="M10 38 L28 20" />
        </>
      )}
      {type === 'sensitive' && (
        <>
          {/* A shield with a ripple: skin that reacts and needs protecting. */}
          <path d="M24 6 L38 11 V23 C38 32 32 39 24 42 C16 39 10 32 10 23 V11 Z" />
          <path d="M16 25 q4 -4 8 0 t8 0" />
        </>
      )}
    </svg>
  );
}
